import { NetworkStatus, useApolloClient, useQuery } from "@apollo/client";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneCallbackIcon from "@mui/icons-material/PhoneCallback";
import PhoneForwardedIcon from "@mui/icons-material/PhoneForwarded";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import { Alert, Box, Chip, CircularProgress, IconButton, Link, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useCanAccess, useTranslate } from "react-admin";
import { Link as RouterLink } from "react-router-dom";

import { ResourceName, TELEPHONY_RESOURCE } from "../../constants/resources";
import {
  CrmObject,
  CrmTimelineKind,
  CustomerServiceCrmTimelineDocument,
  type CustomerServiceCrmTimelineQuery,
  type CustomerServiceCrmTimelineQueryVariables,
} from "../../generated/graphql";
import {
  CRM_COLORS,
  CRM_TIMELINE_COLORS,
  crmRecordPath,
  formatCallDuration,
  formatCrmDateTime,
  formatCrmMonth,
} from "../../lib/crm";
import { trace } from "../../lib/trace";
import { CursorPagination } from "../CursorPagination";
import { PhoneNumberLink } from "../telephony/PhoneNumberLink";
import { CrmCard } from "./CrmCard";
import { CrmRecordProvider } from "./CrmRecordContext";

export type CrmTimelineEntry = CustomerServiceCrmTimelineQuery["customerServiceCrmTimeline"][number];
type TimelineCursor = NonNullable<CustomerServiceCrmTimelineQueryVariables["after"]>;
type TimelinePagination = {
  response?: CustomerServiceCrmTimelineQuery;
  page: number;
  cursors: (TimelineCursor | null)[];
  terminalPage: number | null;
};

const PAGE_SIZE = 30;
const ICON_SIZE = 28;
const PREVIEW_LINES = 2;

const FILTERS: (CrmTimelineKind | "ALL")[] = [
  "ALL",
  CrmTimelineKind.Email,
  CrmTimelineKind.Call,
  CrmTimelineKind.Comment,
  CrmTimelineKind.StatusChange,
];

// A lead has no cases, so its timeline holds only calls.
const LEAD_FILTERS: (CrmTimelineKind | "ALL")[] = ["ALL", CrmTimelineKind.Call];

const KindIcon = ({ entry }: { entry: CrmTimelineEntry }) => {
  const icons: Record<CrmTimelineKind, ReactNode> = {
    [CrmTimelineKind.Email]: <MailOutlineIcon sx={{ fontSize: 16 }} />,
    [CrmTimelineKind.Call]:
      entry.inbound === true ? (
        <PhoneCallbackIcon sx={{ fontSize: 16 }} />
      ) : entry.inbound === false ? (
        <PhoneForwardedIcon sx={{ fontSize: 16 }} />
      ) : (
        <PhoneOutlinedIcon sx={{ fontSize: 16 }} />
      ),
    [CrmTimelineKind.Comment]: <ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />,
    [CrmTimelineKind.StatusChange]: <SyncAltIcon sx={{ fontSize: 16 }} />,
    [CrmTimelineKind.CaseOpened]: <WorkOutlineOutlinedIcon sx={{ fontSize: 16 }} />,
  };
  return (
    <Box
      aria-hidden
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: ICON_SIZE,
        height: ICON_SIZE,
        borderRadius: "50%",
        backgroundColor: CRM_TIMELINE_COLORS[entry.kind],
        color: CRM_COLORS.iconForeground,
        flex: "none",
      }}
    >
      {icons[entry.kind]}
    </Box>
  );
};

const useEntryHeadline = (): ((entry: CrmTimelineEntry) => string) => {
  const translate = useTranslate();
  return entry => {
    const field = entry.field ? translate(`crm.timeline.historyFields.${entry.field}`, { _: entry.field }) : "";
    switch (entry.kind) {
      case CrmTimelineKind.Email:
        if (entry.inbound === true && entry.actor)
          return translate("crm.timeline.emailInbound", { actor: entry.actor });
        if (entry.inbound === false && entry.counterpart)
          return translate("crm.timeline.emailOutbound", { counterpart: entry.counterpart });
        return translate("crm.timeline.emailUnknown");
      case CrmTimelineKind.Call: {
        const direction = translate(
          entry.inbound === true
            ? "crm.timeline.callInbound"
            : entry.inbound === false
              ? "crm.timeline.callOutbound"
              : "crm.timeline.callUnknown"
        );
        return entry.actor ? `${direction} ${translate("crm.timeline.callAgent", { agent: entry.actor })}` : direction;
      }
      case CrmTimelineKind.Comment:
        return entry.actor
          ? translate("crm.timeline.comment", { actor: entry.actor })
          : translate("crm.timeline.commentUnknown");
      case CrmTimelineKind.StatusChange:
        return entry.actor
          ? translate("crm.timeline.statusChangeBy", { field, actor: entry.actor })
          : translate("crm.timeline.statusChange", { field });
      case CrmTimelineKind.CaseOpened:
        return entry.actor
          ? translate("crm.timeline.caseOpenedBy", { actor: entry.actor })
          : translate("crm.timeline.caseOpened");
      default:
        return "";
    }
  };
};

const EntryDetails = ({ entry }: { entry: CrmTimelineEntry }) => {
  const translate = useTranslate();
  const parts: ReactNode[] = [];
  if (entry.kind === CrmTimelineKind.Call) {
    // Without a readable talk time the call shows no duration at all.
    if (entry.durationSeconds === 0) parts.push(translate("crm.timeline.callNotAnswered"));
    else if (entry.durationSeconds) {
      parts.push(translate("crm.timeline.callDuration", { duration: formatCallDuration(entry.durationSeconds) }));
    }
    if (entry.counterpart) {
      parts.push(<PhoneNumberLink key="number" number={entry.counterpart} sx={{ color: CRM_COLORS.link }} />);
    }
    if (entry.title) parts.push(entry.title);
  }
  if (entry.kind === CrmTimelineKind.StatusChange) {
    parts.push(
      translate("crm.timeline.statusChangeValues", {
        oldValue: entry.oldValue || "–",
        newValue: entry.newValue || "–",
      })
    );
  }
  if (entry.kind === CrmTimelineKind.Email && entry.inbound === true && entry.counterpart) {
    parts.push(translate("crm.timeline.to", { address: entry.counterpart }));
  }
  if (parts.length === 0) return null;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px 8px", color: CRM_COLORS.textWeak }}>
      {parts.map((part, index) => (
        // eslint-disable-next-line react/no-array-index-key -- parts have a fixed order per kind
        <Box component="span" key={index} sx={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
          {index > 0 && <FiberManualRecordIcon sx={{ fontSize: 4 }} />}
          {part}
        </Box>
      ))}
      {entry.kind === CrmTimelineKind.Call && entry.provider && (
        <Chip
          size="small"
          label={translate(`crm.timeline.providers.${entry.provider}`)}
          sx={{ height: 18, fontSize: "11px", backgroundColor: CRM_COLORS.recordedBadge }}
        />
      )}
      {entry.kind === CrmTimelineKind.Call && entry.recorded && (
        <Chip
          size="small"
          label={translate("crm.timeline.recorded")}
          sx={{ height: 18, fontSize: "11px", backgroundColor: CRM_COLORS.recordedBadge }}
        />
      )}
    </Box>
  );
};

type TimelineItemProps = {
  entry: CrmTimelineEntry;
  showCase: boolean;
  last: boolean;
};

const TimelineItem = ({ entry, showCase, last }: TimelineItemProps) => {
  const translate = useTranslate();
  const headline = useEntryHeadline();
  const [expanded, setExpanded] = useState(false);
  const subject =
    entry.kind === CrmTimelineKind.Email || entry.kind === CrmTimelineKind.CaseOpened ? entry.title : null;

  return (
    <Box component="li" sx={{ display: "flex", gap: "12px", position: "relative", pb: last ? 0 : "16px" }}>
      {!last && (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            left: ICON_SIZE / 2 - 1,
            top: ICON_SIZE,
            bottom: 0,
            width: "2px",
            backgroundColor: CRM_COLORS.timelineLine,
          }}
        />
      )}
      <KindIcon entry={entry} />
      <Box sx={{ flex: 1, minWidth: 0, fontSize: "13px", color: CRM_COLORS.text }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography component="h3" sx={{ fontSize: "13px", fontWeight: 700, lineHeight: 1.4 }}>
              {subject || headline(entry)}
            </Typography>
            {subject && <Box sx={{ color: CRM_COLORS.textWeak }}>{headline(entry)}</Box>}
          </Box>
          <Box component="time" dateTime={entry.at ?? undefined} sx={{ flex: "none", color: CRM_COLORS.textWeak }}>
            {entry.at ? formatCrmDateTime(entry.at) : ""}
          </Box>
          {entry.body && (
            <IconButton
              size="small"
              onClick={() => setExpanded(current => !current)}
              aria-expanded={expanded}
              aria-label={translate(expanded ? "crm.timeline.collapse" : "crm.timeline.expand")}
              sx={{ mt: "-4px" }}
            >
              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          )}
        </Box>
        <EntryDetails entry={entry} />
        {entry.bodyNotImported && (
          <Box sx={{ mt: "6px", color: CRM_COLORS.textWeak }}>{translate("crm.timeline.emailBodyNotImported")}</Box>
        )}
        {entry.body && (
          <Box
            sx={{
              mt: "6px",
              p: "8px 12px",
              backgroundColor: CRM_COLORS.pageBackground,
              borderRadius: "4px",
            }}
          >
            <Box
              sx={{
                overflowWrap: "anywhere",
                // The preview runs the lines together, as Salesforce does.
                ...(expanded
                  ? { whiteSpace: "pre-wrap" }
                  : {
                      display: "-webkit-box",
                      WebkitLineClamp: PREVIEW_LINES,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }),
              }}
            >
              {entry.body}
            </Box>
          </Box>
        )}
        {showCase && entry.caseId && entry.caseTitle && (
          <Link
            component={RouterLink}
            to={crmRecordPath(CrmObject.Case, entry.caseId)}
            sx={{ display: "inline-block", mt: "4px", fontSize: "12px", color: CRM_COLORS.link }}
          >
            {translate("crm.timeline.inCase", { case: entry.caseTitle })}
          </Link>
        )}
      </Box>
    </Box>
  );
};

// Months follow the Berlin calendar, like the times shown.
const monthKey = (at: string | null | undefined): string => (at ? formatCrmMonth(at) : "");

type CrmTimelineProps = {
  object: CrmObject;
  salesforceId: string;
  title?: string;
  // Inside a record tab the timeline needs no card of its own.
  bare?: boolean;
};

type TimelineEntriesProps = Pick<CrmTimelineProps, "object" | "salesforceId"> & {
  filter: CrmTimelineKind | "ALL";
};

const TimelineEntries = ({ object, salesforceId, filter }: TimelineEntriesProps) => {
  const translate = useTranslate();
  const client = useApolloClient();
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);
  const [pagination, setPagination] = useState<TimelinePagination>({ page: 1, cursors: [null], terminalPage: null });
  const { page, cursors, terminalPage } = pagination;
  const inFlight = useRef(false);
  const requestGeneration = useRef(0);
  const baseRefetching = useRef(false);
  const variables: CustomerServiceCrmTimelineQueryVariables = {
    object,
    salesforceId,
    kinds: filter === "ALL" ? null : [filter],
    limit: PAGE_SIZE,
  };
  const variablesKey = JSON.stringify(variables);
  const {
    data: baseData,
    loading,
    error,
    networkStatus,
  } = useQuery<CustomerServiceCrmTimelineQuery, CustomerServiceCrmTimelineQueryVariables>(
    CustomerServiceCrmTimelineDocument,
    {
      variables,
      fetchPolicy: "no-cache",
      notifyOnNetworkStatusChange: true,
    }
  );
  if (networkStatus === NetworkStatus.refetch && !baseRefetching.current) {
    baseRefetching.current = true;
    requestGeneration.current += 1;
    inFlight.current = false;
    setLoadingMore(false);
  } else if (networkStatus !== NetworkStatus.refetch && baseRefetching.current) {
    baseRefetching.current = false;
    if (networkStatus === NetworkStatus.ready && !error && baseData) {
      setPagination({ page: 1, cursors: [null], terminalPage: null });
      setMoreFailed(false);
    }
  }
  const data = pagination.response ?? baseData;
  const entries = useMemo(() => data?.customerServiceCrmTimeline ?? [], [data]);
  const showCase = object !== CrmObject.Case;
  // A page that arrives after the filter or record changed belongs to entries no longer shown.
  const shownKey = useRef<string | null>(variablesKey);
  shownKey.current = variablesKey;
  useEffect(() => {
    shownKey.current = variablesKey;
    return () => {
      shownKey.current = null;
    };
  }, [variablesKey]);
  const hasNextPage = entries.length >= PAGE_SIZE && terminalPage !== page;
  const state = loading || loadingMore ? "loading" : error || moreFailed ? "error" : "ready";
  useEffect(() => {
    trace("ui", "crm-timeline-query", {}, `state=${state}`);
  }, [state]);

  const changePage = async (next: number) => {
    const last = entries[entries.length - 1];
    if (!last || inFlight.current || loading || next === page || next < 1) return;
    const newPage = next === page + 1 && next === cursors.length + 1;
    const after = newPage ? { at: last.at, kind: last.kind, salesforceId: last.salesforceId } : cursors[next - 1];
    if (after === undefined || (newPage && !hasNextPage)) return;
    const requested = variablesKey;
    const generation = requestGeneration.current;
    inFlight.current = true;
    setLoadingMore(true);
    setMoreFailed(false);
    const requestPage = (cursor: TimelineCursor | null) =>
      client.query<CustomerServiceCrmTimelineQuery, CustomerServiceCrmTimelineQueryVariables>({
        query: CustomerServiceCrmTimelineDocument,
        variables: { ...variables, after: cursor },
        fetchPolicy: "no-cache",
      });
    try {
      const result = await requestPage(after);
      if (requested !== shownKey.current || generation !== requestGeneration.current) return;
      if (next > page && result.data.customerServiceCrmTimeline.length === 0) {
        setPagination(current => ({ ...current, terminalPage: page }));
        return;
      }
      if (next > 1 && result.data.customerServiceCrmTimeline.length === 0) {
        const first = await requestPage(null);
        if (requested !== shownKey.current || generation !== requestGeneration.current) return;
        setPagination({ response: first.data, page: 1, cursors: [null], terminalPage: null });
        return;
      }
      setPagination({
        response: result.data,
        page: next,
        cursors: newPage ? [...cursors, after] : cursors,
        terminalPage,
      });
    } catch {
      if (requested === shownKey.current && generation === requestGeneration.current) setMoreFailed(true);
    } finally {
      if (requested === shownKey.current && generation === requestGeneration.current) {
        inFlight.current = false;
        setLoadingMore(false);
      }
    }
  };

  const months: { key: string; entries: CrmTimelineEntry[] }[] = [];
  for (const entry of entries) {
    const key = monthKey(entry.at);
    const current = months[months.length - 1];
    if (current?.key === key) current.entries.push(entry);
    else months.push({ key, entries: [entry] });
  }

  return (
    <>
      {(error || moreFailed) && <Alert severity="error">{translate("crm.loadError")}</Alert>}
      {loading && entries.length === 0 && <CircularProgress size={24} aria-label={translate("crm.loading")} />}
      {!loading && !error && entries.length === 0 && (
        <Typography sx={{ fontSize: "13px", color: CRM_COLORS.textWeak }}>{translate("crm.timeline.empty")}</Typography>
      )}
      {months.map(month => (
        <Box key={month.key} component="section" sx={{ mb: "12px" }}>
          <Typography
            component="h3"
            sx={{
              mb: "8px",
              py: "4px",
              px: "8px",
              fontSize: "13px",
              fontWeight: 700,
              backgroundColor: CRM_COLORS.pageBackground,
              borderRadius: "4px",
            }}
          >
            {month.key}
          </Typography>
          <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0 }}>
            {month.entries.map((entry, index) => (
              <TimelineItem
                key={`${entry.kind}-${entry.salesforceId}`}
                entry={entry}
                showCase={showCase}
                last={index === month.entries.length - 1}
              />
            ))}
          </Box>
        </Box>
      ))}
      {data && (
        <CursorPagination
          page={page}
          perPage={PAGE_SIZE}
          itemCount={entries.length}
          hasNextPage={hasNextPage}
          isPending={loading || loadingMore}
          resource={ResourceName.Crm}
          onPageChange={next => void changePage(next)}
        />
      )}
      <Typography sx={{ mt: "8px", fontSize: "11px", color: CRM_COLORS.label }}>
        {translate("crm.timeline.window")}
      </Typography>
    </>
  );
};

export const CrmTimeline = ({ object, salesforceId, title, bare = false }: CrmTimelineProps) => {
  const translate = useTranslate();
  // The backend leaves calls out for staff who may not phone, so their filter would only be empty.
  const { canAccess: mayPhone } = useCanAccess({ action: "list", resource: TELEPHONY_RESOURCE });
  const allFilters = object === CrmObject.Lead ? LEAD_FILTERS : FILTERS;
  const filters = mayPhone ? allFilters : allFilters.filter(kind => kind !== CrmTimelineKind.Call);
  const [filter, setFilter] = useState<CrmTimelineKind | "ALL">("ALL");
  const content = (
    <CrmRecordProvider object={object} salesforceId={salesforceId}>
      {filters.length > 1 && (
        <Box
          role="group"
          aria-label={translate("crm.timeline.filterLabel")}
          sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mb: "12px" }}
        >
          {filters.map(kind => (
            <Chip
              key={kind}
              label={translate(`crm.timeline.filters.${kind}`)}
              size="small"
              clickable
              color={filter === kind ? "primary" : "default"}
              variant={filter === kind ? "filled" : "outlined"}
              aria-pressed={filter === kind}
              onClick={() => setFilter(kind)}
            />
          ))}
        </Box>
      )}
      <TimelineEntries
        key={`${object}|${salesforceId}|${filter}`}
        object={object}
        salesforceId={salesforceId}
        filter={filter}
      />
    </CrmRecordProvider>
  );
  if (bare) return <Box data-testid="crm-timeline">{content}</Box>;
  return (
    <CrmCard title={title ?? translate("crm.timeline.title")} testId="crm-timeline">
      {content}
    </CrmCard>
  );
};
